import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Users, 
  Award, 
  Target, 
  BookOpen, 
  Globe,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Lightbulb,
  FileText,
  DollarSign,
  Mail,
  GraduationCap,
  Heart,
  Briefcase,
  Database,
  Loader2
} from 'lucide-react';
import { useFormSubmit } from '../../hooks/useFormSubmit';
import FormNotification from '../../components/FormNotification';

const IACenidInvestigacion = () => {
  const { loading, success, error, submitForm, resetForm } = useFormSubmit('ia-cenid-investigacion');
  
  // Estado para datos dinámicos
  const [upeaceData, setUpeaceData] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Cargar datos del JSON
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [upeaceResponse, generalesResponse] = await Promise.all([
          fetch(`/data/iacenid-investigacion.json?v=${Date.now()}`),
          fetch(`/data/generales.json?v=${Date.now()}`)
        ]);
        
        if (!upeaceResponse.ok || !generalesResponse.ok) {
          throw new Error('Error al cargar los datos');
        }
        
        const upeaceData = await upeaceResponse.json();
        const generalesData = await generalesResponse.json();
        
        setUpeaceData(upeaceData);
        setDatosBancarios(generalesData.datosBancarios);
      } catch (error) {
        console.error('Error al cargar datos de IA-CENID:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    cargarDatos();
  }, []);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    pais: '',
    institucion: '',
    mensaje: ''
  });

  // Manejar cambios en los campos
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submitForm(formData);
    
    if (result.success) {
      // Limpiar formulario después de envío exitoso
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        pais: '',
        institucion: '',
        mensaje: ''
      });
      
      // Auto-cerrar mensaje de éxito después de 5 segundos
      setTimeout(() => {
        resetForm();
      }, 5000);
    }
  };
  const modulos = [
    {
      numero: 1,
      nombre: 'Método de investigación IDA con IA y proyectos de investigación con IA. Revisión Bibliográfica Eficiente',
      horas: 8,
      descripcion: 'Método IDA, panorama de la IA en los proyectos de investigación. Búsqueda avanzada de literatura: Mapeo de fuentes y tendencias. Organización y gestión de colecciones de artículos. Búsqueda de autores relevantes en los temas de investigación. Mejora de la coherencia, gramática y estilo académico; Uso de prompts efectivos para la escritura.',
      herramientas: 'Elicit, Consensus, ResearchRabbit, Perplexity, ChatGPT/Gemini, WordVice AI, Trinka',
      icono: BookOpen,
      color: 'blue',
      horasSemanales: '2H Síncronas, 4H Asíncronas'
    },
    {
      numero: 2,
      nombre: 'Metodología de la investigación en una página con IA',
      horas: 6,
      descripcion: 'Proceso de investigación en una página con IA.',
      herramientas: 'SciSpace, Perplexity',
      icono: FileText,
      color: 'green',
      horasSemanales: '2H Síncronas, 4H Asíncronas'
    },
    {
      numero: 3,
      nombre: 'Análisis y Extracción de Datos de Documentos',
      horas: 6,
      descripcion: 'Interacción y resumen de PDFs. Extracción de referencias, figuras y datos clave. Análisis comparativo sistemático (tablas de hallazgos).',
      herramientas: 'ChatPDF, Schorlacx, NotebookLM (Google)',
      icono: Database,
      color: 'purple',
      horasSemanales: '2H Síncronas, 4H Asíncronas'
    },
    {
      numero: 4,
      nombre: 'Análisis estadístico cuantitativo y cualitativo',
      horas: 6,
      descripcion: 'Análisis estadísticos para los proyectos cuantitativos y análisis cualitativo para los proyectos de investigación cualitativa.',
      herramientas: 'Julius, GPT 5, DeepSeek, Gemini, Grok',
      icono: TrendingUp,
      color: 'orange',
      horasSemanales: '2H Síncronas, 4H Asíncronas'
    },
    {
      numero: 5,
      nombre: 'Asistencia en la Redacción Científica Formato y Referenciación Automatizada',
      horas: 6,
      descripcion: 'Adaptación de manuscritos a plantillas de revistas indexadas (formato, estilo). Generación y verificación de citas y referencias bibliográficas en diversos formatos.',
      herramientas: 'Typeset.io, SciSpace (Citation Generator)',
      icono: Briefcase,
      color: 'red',
      horasSemanales: '2H Síncronas, 4H Asíncronas'
    },
    {
      numero: 6,
      nombre: 'Metodología de la investigación cualitativa con IA',
      horas: 6,
      descripcion: 'Aplicación de la metodología de la investigación cualitativa en una página con IA dentro del proceso dinámico, armónico, sistemático y holístico de las investigaciones cualitativas.',
      herramientas: '',
      icono: Lightbulb,
      color: 'indigo',
      horasSemanales: '6H Síncronas, 4H Asíncronas'
    },
    {
      numero: 7,
      nombre: 'Visualización de Datos e Imágenes para Publicación. Ética y Fundamentos de la IA, Detección y Prevención de Plagio y Uso Indebido',
      horas: 6,
      descripcion: 'Creación de gráficos y figuras claras. Consideraciones éticas para imágenes generadas por IA. Optimización de imágenes para requisitos de revistas. Herramientas para detectar contenido generado por IA. Buenas prácticas para el uso responsable y transparente de la IA. Implicaciones éticas, autoría y plagio. Lineamientos de revistas indexadas sobre el uso de IA.',
      herramientas: 'DALL-E 2/Midjourney, SciSpace (AI Detector), Turnitin (si aplica)',
      icono: Heart,
      color: 'pink',
      horasSemanales: '2H Síncronas, 4H Asíncronas'
    },
    {
      numero: 8,
      nombre: 'Taller Integrador y Estrategias de Publicación',
      horas: 16,
      descripcion: 'Aplicación de las herramientas en un proyecto real. Respuesta a revisores asistida por IA. Estrategias para aumentar la citación e impacto.',
      herramientas: 'Todas las herramientas anteriores, Plataformas de envío',
      icono: Award,
      color: 'yellow',
      horasSemanales: '6 horas (2H Síncronas, 12H Asíncronas)'
    }
  ];

  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50',
    green: 'border-[#0b742f] bg-green-50',
    purple: 'border-purple-500 bg-purple-50',
    orange: 'border-[#e87729] bg-orange-50',
    red: 'border-red-500 bg-red-50',
    indigo: 'border-indigo-500 bg-indigo-50',
    pink: 'border-pink-500 bg-pink-50',
    yellow: 'border-yellow-500 bg-yellow-50'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <GraduationCap className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {isLoadingData ? (
                  <Loader2 className="w-8 h-8 animate-spin inline-block" />
                ) : (
                  upeaceData?.hero?.titulo || 'Certificación de IA en la Investigación y Publicación Científica'
                )}
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-4xl mb-6">
                {isLoadingData ? (
                  <Loader2 className="w-6 h-6 animate-spin inline-block" />
                ) : (
                  upeaceData?.hero?.descripcion || 'Programa de certificación en Herramientas de Inteligencia Artificial (IA) para la Investigación Científica y Publicación en Revistas Indexadas.'
                )}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm md:text-base">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Clock className="w-5 h-5" />
                  {isLoadingData ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span>Duración: {upeaceData?.curso?.duracion || '60 horas'}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Globe className="w-5 h-5" />
                  {isLoadingData ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span>Modalidad: {upeaceData?.curso?.modalidad || '100% Virtual'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        
        {/* Tarjetas de información rápida */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Perfil del participante */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-[#0b742f] hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <Users className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Perfil del participante</h3>
            </div>
            <p className="text-gray-600">Profesores e investigadores de Latinoamérica interesados en aplicar IA a la investigación científica y publicación en revistas indexadas.</p>
          </div>

          {/* Modalidad */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-[#e87729] hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#e87729]/10 p-3 rounded-lg">
                <Globe className="w-6 h-6 text-[#e87729]" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Modalidad</h3>
            </div>
            <p className="text-gray-600">{isLoadingData ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : (upeaceData?.curso?.modalidad || '100% Virtual')}</p>
          </div>

          {/* Certificación */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-500 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-500/10 p-3 rounded-lg">
                <Award className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Certificación</h3>
            </div>
            <p className="text-gray-600">Se otorga al completar el 80% de asistencia y aprobar el proyecto final integrador.</p>
          </div>
        </div>

        {/* Descripción del Programa */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-[#0b742f]">
            <div className="mb-4">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                <Target className="w-8 h-8 text-[#0b742f]" />
                Descripción del Programa
              </h2>
              <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
            </div>
            <p className="text-gray-700 leading-relaxed text-justify mb-4">
              Programa de certificación en <strong>Herramientas de Inteligencia Artificial (IA) para la Investigación Científica y Publicación en Revistas Indexadas</strong> centrado en la aplicación práctica para profesores e investigadores de Latinoamérica, combinando capacitación virtual y actividades en línea.
            </p>
            <div className="bg-green-50 border-l-4 border-[#0b742f] p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <strong>Duración total:</strong> {isLoadingData ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : (upeaceData?.curso?.duracion || '60 horas')}, distribuidas en {isLoadingData ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : (upeaceData?.curso?.distribucion || '8 semanas (6 horas semanales, incluyendo sesiones síncronas y actividades asíncronas para el proyecto final de 12 horas)')}.
              </p>
            </div>
          </div>
        </section>

        {/* Metodología */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-[#e87729]">
            <div className="mb-4">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                <BookOpen className="w-8 h-8 text-[#e87729]" />
                Metodología Sugerida
              </h2>
              <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
            </div>
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Globe className="w-6 h-6 text-[#0b742f]" />
                  Modalidad
                </h3>
                {isLoadingData ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: upeaceData?.curso?.modalidad || '100% Virtual' }} />
                )}
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="w-6 h-6 text-[#e87729]" />
                  Encuentros Síncronos (Videoconferencias)
                </h3>
                {isLoadingData ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: upeaceData?.metodologia?.encuentrosSincronos || 'Sesiones en vivo para explicación conceptual, demostraciones de herramientas, discusión ética y resolución de dudas.' }} />
                )}
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-blue-600" />
                  Actividades Asíncronas (Online)
                </h3>
                {isLoadingData ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: upeaceData?.metodologia?.actividadesAsincronas || 'Lecturas complementarias, foros de discusión, tutoriales de herramientas, y tareas prácticas de aplicación directa en un manuscrito de investigación del participante.' }} />
                )}
              </div>

              {/*
              <div className="bg-green-50 border-l-4 border-[#0b742f] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-[#0b742f]" />
                  Libro de apoyo
                </h3>
                {isLoadingData ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: upeaceData?.metodologia?.libroApoyo || 'Investigación en una página con IA, del Autor Luis Alfonso Pérez Romero, Editorial ECOE, 2025.' }} />
                )}
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Award className="w-6 h-6 text-blue-600" />
                  Certificación
                </h3>
                {isLoadingData ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: upeaceData?.curso?.certificacion?.requisito || 'Se otorga a quienes completen el 80% de la asistencia a sesiones asíncronas y aprueben el proyecto final integrador (ejercicio de aplicación con una investigación propia).' }} />
                )}
              </div>

                */}


            </div>
          </div>
        </section>

        {/* Plan de Estudios */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#e87729]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <BookOpen className="w-8 h-8 text-[#e87729]" />
                  Programa de Certificación: IA en la Investigación Científica
                </h2>
                <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
                <p className="text-gray-600 mt-4">
                  Total: {isLoadingData ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : (upeaceData?.curso?.duracion || '60 horas')} distribuidas en {isLoadingData ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : (upeaceData?.curso?.distribucion || '8 semanas')}
                </p>
              </div>

              <div className="space-y-4">
                {modulos.map((modulo) => {
                  const IconComponent = modulo.icono;
                  return (
                    <div 
                      key={modulo.numero} 
                      className={`border-l-4 ${colorClasses[modulo.color]} p-6 rounded-r-lg hover:shadow-md transition-shadow duration-200`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <IconComponent className="w-6 h-6 text-gray-700" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2 gap-2">
                            <h3 className="text-lg font-bold text-gray-900">
                              Módulo {modulo.numero}: {modulo.nombre}
                            </h3>
                            <div className="flex flex-col gap-1">
                              <span className="text-sm font-semibold text-gray-600 bg-white px-3 py-1 rounded-full whitespace-nowrap">
                                {modulo.horas} horas
                              </span>
                              {modulo.horasSemanales && (
                                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">
                                  {modulo.horasSemanales}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-gray-700 mb-2" dangerouslySetInnerHTML={{ __html: modulo.descripcion }} />
                          {modulo.herramientas && (
                            <div className="bg-white/50 p-3 rounded-lg mt-2">
                              <div className="text-sm text-gray-600">
                                <strong className="text-gray-800">Herramientas de IA:</strong>{' '}
                                <span dangerouslySetInnerHTML={{ __html: modulo.herramientas }} />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Total */}
                <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">TOTAL</h3>
                    <span className="text-2xl font-bold">60 horas</span>
                  </div>
                  <p className="text-white/90 mt-2">16 horas síncronas, 44 horas asíncronas</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Justificación de Herramientas */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Lightbulb className="w-8 h-8 text-[#0b742f]" />
                  Justificación de Herramientas
                </h2>
                <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
              </div>
              <div className="bg-green-50 border-l-4 border-[#0b742f] p-6 rounded-r-lg">
                <p className="text-gray-700 leading-relaxed">
                  {isLoadingData ? (
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  ) : (
                    upeaceData?.justificacion || 'Las herramientas seleccionadas como ChatGPT/Gemini, DeepSeek, Manus, Kimi, Claude, Copilot, Grok, Elicit, Perplexity, Consensus, SciSpace, ResearchRabbit y otras, son algunas de las más mencionadas en el ámbito académico por su utilidad en distintas fases de la investigación y publicación, abarcando desde la revisión de literatura hasta la edición de manuscritos. La inclusión de herramientas para la detección de IA y el módulo de Ética es crucial para asegurar la integridad académica exigida por las revistas indexadas.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Productos a Entregar 
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#e87729]">
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Award className="w-8 h-8 text-[#e87729]" />
                  Productos a Entregar
                </h2>
                <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
                <p className="text-gray-600 mt-4">
                  Los productos que entregará el CENID a cada participante de la certificación:
                </p>
              </div>
              {isLoadingData ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0b742f]" />
                </div>
              ) : (
                <div className="space-y-3">
                  {(upeaceData?.productos || []).map((producto, index) => (
                    <div key={index} className="flex items-start gap-3 bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border-2 border-gray-200 hover:border-[#e87729] hover:shadow-md transition-all duration-200">
                      <CheckCircle className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-1" />
                      <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: producto }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
        */}

        {/* Calendario de Cohortes */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Calendar className="w-8 h-8 text-[#0b742f]" />
                  Calendario de Cohortes
                </h2>
                <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
              </div>
              
              {isLoadingData ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0b742f]" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(upeaceData?.cohortes || []).map((cohorte, index) => {
                    const colores = [
                      'from-orange-500 to-orange-600',
                      'from-blue-500 to-blue-600',
                      'from-green-500 to-green-600',
                      'from-purple-500 to-purple-600'
                    ];
                    return (
                      <div key={index} className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                        <div className={`bg-gradient-to-r ${colores[index % 4]} text-white px-4 py-2 rounded-lg mb-4 inline-block`}>
                          <h3 className="text-xl font-bold">{cohorte.nombre}</h3>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <Calendar className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-1" />
                            <div>
                              <p className="text-sm text-gray-600 font-semibold">Periodo de inscripción</p>
                              <p className="text-gray-800">{cohorte.inscripcion.inicio}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-1" />
                            <div>
                              <p className="text-sm text-gray-600 font-semibold">Inicio y fin</p>
                              <p className="text-gray-800">{cohorte.periodo.inicio} al {cohorte.periodo.fin}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Inversión y Público Objetivo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Inversión */}
          <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-[#0b742f]">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <DollarSign className="w-8 h-8 text-[#0b742f]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Inversión</h3>
            </div>
            {isLoadingData ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-[#0b742f]" />
              </div>
            ) : (
              <>
                <p className="text-3xl font-bold text-[#0b742f] mb-2">
                  ${upeaceData?.curso?.inversion?.monto || '320'} {upeaceData?.curso?.inversion?.moneda || 'USD'}
                </p>
                <p className="text-gray-600 mb-4">
                  {upeaceData?.curso?.inversion?.descripcion || 'Pago único por certificación completa'}
                </p>
                
                {/* Datos Bancarios */}
                {datosBancarios && (
                  <div className="mt-6 border-t-2 border-gray-200 pt-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Database className="w-5 h-5 text-[#0b742f]" />
                      Datos para transferencia bancaria
                    </h4>
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-gray-600 text-xs mb-1">Banco:</p>
                        <p className="font-semibold text-gray-900">{datosBancarios.banco}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-gray-600 text-xs mb-1">Titular:</p>
                        <p className="font-semibold text-gray-900">{datosBancarios.titular}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-gray-600 text-xs mb-1">Número de cuenta:</p>
                        <p className="font-semibold text-gray-900">{datosBancarios.numeroCuenta}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-gray-600 text-xs mb-1">CLABE:</p>
                        <p className="font-semibold text-gray-900">{datosBancarios.clabe}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-600 text-xs mb-1">SWIFT:</p>
                          <p className="font-semibold text-gray-900">{datosBancarios.swift}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-600 text-xs mb-1">Sucursal:</p>
                          <p className="font-semibold text-gray-900">{datosBancarios.sucursal}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Leyenda de comprobante de pago */}
                    <div className="mt-4 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r">
                      <p className="text-base text-gray-700">
                        <strong>Importante:</strong> Por favor, envíe su comprobante de pago al correo{' '}
                        <br />
                        <a 
                          href={`mailto:${upeaceData?.contacto?.emailPagos || 'universityforpeace@cenid.org'}`}
                          className="text-[#0b742f] font-semibold hover:underline"
                        >
                          {upeaceData?.contacto?.emailPagos || 'universityforpeace@cenid.org'}
                        </a>
                        {' '}para confirmar su inscripción.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Público Objetivo */}
          <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-[#e87729]">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#e87729]/10 p-3 rounded-lg">
                <Users className="w-8 h-8 text-[#e87729]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Público objetivo</h3>
            </div>
            <ul className="space-y-3">
              {[
                'Profesores universitarios',
                'Investigadores académicos',
                'Estudiantes de posgrado',
                'Profesionales interesados en investigación científica y publicación en revistas indexadas'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Formulario de Inscripción */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Mail className="w-8 h-8 text-[#0b742f]" />
                  Inscripción
                </h2>
                <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
              </div>

              {/* Notificaciones */}
              {success && (
                <FormNotification
                  type="success"
                  message="¡Solicitud enviada exitosamente! Nos pondremos en contacto contigo pronto."
                  onClose={resetForm}
                />
              )}
              
              {error && (
                <FormNotification
                  type="error"
                  message={error}
                  onClose={resetForm}
                />
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Tu nombre completo"
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Correo electrónico *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="tu@email.com"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="+52 123 456 7890"
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      País *
                    </label>
                    <input
                      type="text"
                      name="pais"
                      value={formData.pais}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="México"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Institución *
                  </label>
                  <input
                    type="text"
                    name="institucion"
                    value={formData.institucion}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Universidad o institución"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mensaje
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Cuéntanos sobre tu interés en el programa..."
                    disabled={loading}
                  ></textarea>
                </div>

                <div className="flex flex-wrap gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Enviar solicitud
                      </>
                    )}
                  </button>
                  
                  <a
                    href={`mailto:${upeaceData?.contacto?.email || 'universityforpeace@cenid.org'}?subject=Información sobre Certificación IA`}
                    className="bg-gradient-to-r from-[#e87729] to-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
                  >
                    <Mail className="w-5 h-5" />
                    Más información
                  </a>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Llamada a la Acción Final */}
        <section>
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              ¿Listo para certificarte en IA para la investigación?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Únete al programa de Certificación de IA en la Investigación y Publicación Científica. Domina las herramientas de IA más avanzadas y publica en revistas indexadas.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="bg-white text-[#0b742f] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-200 flex items-center gap-2">
                Inscribirme ahora
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="https://www.reci.org.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-lg font-bold hover:bg-white/20 transition-colors duration-200"
              >
                Ver revista RECI
              </a>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default IACenidInvestigacion;
